import { faLock, faSearch, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import HabitatStatus from 'Components/HabitatCard/HabitatStatus';
import { differenceInMinutes } from 'date-fns';
import { Box, Grommet, grommet, Image, TextInput } from 'grommet';
import { deepMerge } from 'grommet/utils';
import _ from 'lodash-es';
import { h } from 'preact';
import { memo } from 'preact/compat';
import { useCallback, useMemo, useRef, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { buildURL } from 'Shared/fetch';
import useFetch from 'use-http';

import HabitatLink from '@/components/HabitatLink';
import { routeToHabitat } from '@/components/HabitatLink/utils';
import { useIsMobileLayout } from '@/components/LayoutDetector/hooks';
import zoolifeTheme from '@/grommetTheme';
import { useWindowResize } from '@/hooks';
import { setHabitats } from '@/redux/actions';
import { useSelector } from '@/redux/helper';
import { createSortedHabitatsSelector, selectAllFreemiumHabitatsMap } from '@/redux/selectors/habitats';
import { trackMP } from '@/shared/mp';

import style from './style.scss';

const theme = deepMerge(grommet, zoolifeTheme, {
  global: {
    drop: {
      shadowSize: 'medium',
      extend: `
        border-bottom-left-radius: 12px;
        border-bottom-right-radius: 12px;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        overflow: hidden;
      `,
    },
  },
  button: {
    extend: 'border-radius: 0;',
  },
});

/**
 * Function for rendering a search component with various functionalities.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.className]
 * @param {'left' | 'right'} [props.iconsPosition]
 * @param {boolean} [props.switchableSearch]
 * @param {boolean} [props.searchShow]
 * @param {(val: boolean) => void} [props.setSearchShow]
 * @param {() => void} [props.onClickSuggestion]
 * @return {JSX.Element} The rendered search component.
 */
const Search = ({
  className,
  iconsPosition = 'right',
  switchableSearch = false,
  searchShow = true,
  setSearchShow,
  onClickSuggestion,
}) => {
  const isMobile = useIsMobileLayout();
  const dispatch = useDispatch();
  const selectSortedHabitats = useMemo(() => createSortedHabitatsSelector(), []);
  const sortedHabitats = useSelector(selectSortedHabitats);
  const role = useSelector((state) => state.user.role);
  const freemiumHabitatsMap = useSelector(selectAllFreemiumHabitatsMap);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [value, setValue] = useState('');
  const boxRef = useRef();
  const lastFetchRef = useRef();
  const { width: windowWidth } = useWindowResize();
  const { get, response } = useFetch(buildURL('/habitats/all'), {
    credentials: 'include',
    cachePolicy: 'no-cache',
  });

  const onSuggestionsOpen = useCallback(() => setSuggestionOpen(true), []);
  const onSuggestionsClose = useCallback(() => setSuggestionOpen(false), []);

  const onSearchFocus = async () => {
    const now = new Date();
    // refresh habitats on focus if data is older than a minute
    if (!lastFetchRef.current || differenceInMinutes(now, lastFetchRef.current) > 1) {
      lastFetchRef.current = now;
      await get();
      if (response.ok) {
        dispatch(setHabitats(response.data.habitats));
      }
    }
  };

  const handleSearchShow = () => {
    setSearchShow?.(true);
    setTimeout(() => {
      boxRef.current.querySelector('input').focus();
    });
  };

  const onChange = useCallback(({ target }) => {
    setValue(target.value);
  }, []);

  const onHabitatClick = useCallback(
    ({ id }) => {
      setValue('');
      onClickSuggestion?.();
      trackMP('visit-habitat-link-clicked', {
        habitatId: id,
        entryPoint: 'search-result',
      });
    },
    [onClickSuggestion],
  );

  const suggestions = useMemo(() => {
    const input = value.trim().toLowerCase();
    const isGeneralUser = ['user', 'vip'].includes(role);

    return _.chain(sortedHabitats)
      .map((habitat) => {
        const { title, animal, hidden, zoo, keywords, isStreamOn } = habitat;
        if (hidden || (!input && !isStreamOn)) {
          return null;
        }
        if (!input) {
          return [habitat, 0];
        }
        if (title?.toLowerCase().includes(input)) {
          return [habitat, 1];
        }
        if (animal?.toLowerCase().includes(input)) {
          return [habitat, 2];
        }
        if (zoo.name.toLowerCase().includes(input)) {
          return [habitat, 3];
        }
        // we converted the keywords to lower case when we saved them in the DB
        if (keywords?.some((keyword) => keyword.includes(input))) {
          return [habitat, 4];
        }
        return null;
      })
      .filter((data) => data != null)
      .sort((a, b) => a[1] - b[1]) // sort by sortingOrder
      .map(([{ _id, profileImage, title, isStreamOn, liveTalk, slug, zoo, isLocked }], ind, list) => ({
        value: _id,
        onClick: () => {
          routeToHabitat({ id: _id, slug, zooSlug: zoo.slug });
          onHabitatClick({ id: _id });
        },
        label: (
          <HabitatLink
            membersOnlyWarningType="habitat"
            className={style.suggestion}
            habitatId={_id}
            slug={slug}
            zooSlug={zoo.slug}
          >
            <div
              className={clsx(
                'preflight flex items-center gap-1.5 p-3',
                ind < list.length - 1 ? 'border-b border-black/35' : '',
              )}
            >
              <div className="size-[30px] shrink-0">
                <Image
                  src={profileImage}
                  style={{ borderRadius: '100%' }}
                  className={clsx(style.img, {
                    [style.liveTalk]: windowWidth < 460 && liveTalk,
                    [style.online]: windowWidth < 460 && isStreamOn,
                    [style.offline]: windowWidth < 460 && !isStreamOn,
                  })}
                />
              </div>
              <span className="line-clamp-2 min-w-0 grow text-sm font-medium">
                {title}
                {isLocked && (
                  <>
                    {' '}
                    <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#C89400] text-center text-white">
                      <FontAwesomeIcon icon={faLock} className="text-[10px]" />
                    </span>
                  </>
                )}
              </span>
              <HabitatStatus
                online={isStreamOn}
                liveTalk={liveTalk}
                free={isGeneralUser && !freemiumHabitatsMap[_id]}
                className="mr-1.5 shrink-0"
              />
            </div>
          </HabitatLink>
        ),
      }))
      .slice(0, 5)
      .value();
  }, [value, role, sortedHabitats, onHabitatClick, windowWidth, freemiumHabitatsMap]);

  const icons = switchableSearch ? (
    <Box onClick={() => setSearchShow?.(false)} className={style.searchClickIndicator}>
      <FontAwesomeIcon icon={faTimes} color="var(--grey)" style={{ width: '14px' }} />
    </Box>
  ) : (
    <Box onClick={() => boxRef.current.querySelector('input').focus()} className={style.searchClickIndicator}>
      <FontAwesomeIcon icon={faSearch} color="var(--grey)" style={{ width: '14px' }} />
    </Box>
  );

  /** @type {import('grommet').TextInputProps['onSelect']} */
  const onSelect = (e) => {
    e.suggestion.onClick?.();
    boxRef.current.querySelector('input').blur();
  };

  return (
    <Grommet theme={theme} className="flex-grow">
      {searchShow && (
        <Box
          ref={boxRef}
          direction="row"
          align="center"
          round="20px"
          className={clsx(style.searchWrapper, className)}
          pad={{ horizontal: 'small' }}
          elevation={suggestionOpen ? 'medium' : undefined}
          border={{
            side: 'all',
            color: suggestionOpen ? 'transparent' : '#CDCDCD',
          }}
          style={
            suggestionOpen
              ? {
                  borderBottomLeftRadius: '0px',
                  borderBottomRightRadius: '0px',
                }
              : undefined
          }
        >
          {iconsPosition === 'left' && icons}
          <TextInput
            name="search-habitat"
            autoComplete="off"
            dropTarget={boxRef.current}
            dropProps={{
              ref: (element) => {
                const rect = boxRef.current?.getBoundingClientRect();
                if (element && rect) {
                  // eslint-disable-next-line no-param-reassign
                  element.style.width = `${rect.width}px`;
                  // eslint-disable-next-line no-param-reassign
                  element.style.left = `${rect.left}px`;
                }
              },
              ...(isMobile ? { style: { zIndex: 31 } } : undefined),
            }}
            placeholder="Search for an animal..."
            plain
            suggestions={suggestions}
            value={value}
            onChange={onChange}
            onSuggestionsOpen={onSuggestionsOpen}
            onSuggestionsClose={onSuggestionsClose}
            onFocus={onSearchFocus}
            onSelect={onSelect}
            onKeyDown={(e) => {
              // Only trigger when there is only one result and the user presses enter
              // If there are more than one result, the user can use the up and down arrows to select the desired result
              if (e.key === 'Enter' && suggestions.length === 1) {
                [e.suggestion] = suggestions;
                onSelect(e);
              }
            }}
            style={{ padding: '7px', fontWeight: '500' }}
            dropHeight="medium"
          />
          {iconsPosition === 'right' && icons}
        </Box>
      )}
      {!searchShow && (
        <Box onClick={handleSearchShow} className={style.searchClickIndicator} align="end">
          <FontAwesomeIcon icon={faSearch} color="var(--grey)" style={{ width: '14px' }} />
        </Box>
      )}
    </Grommet>
  );
};

export default memo(Search);
